/**
 * TeamMemberList (AC-891)
 *
 * Displays a list of team members with role badges and action buttons.
 *
 * - Owner badge (Crown icon) for the team owner
 * - Member badge (User icon) for regular members
 * - "Remove" button shown to the owner for non-owner members
 * - "Leave Team" button shown to non-owner members for their own row
 * - Owner does NOT see "Leave" or "Remove" on themselves
 *
 * Parent: TeamDetailPage
 */

import React from 'react';
import { Crown, User, UserMinus, LogOut } from 'lucide-react';
import { TeamMember } from '../../types';

interface TeamMemberListProps {
  members: TeamMember[];
  currentUserId: number;
  isOwner: boolean;
  onRemoveMember: (userId: number) => void;
  onLeaveTeam: () => void;
}

const TeamMemberList: React.FC<TeamMemberListProps> = ({
  members,
  currentUserId,
  isOwner,
  onRemoveMember,
  onLeaveTeam,
}) => {
  return (
    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
      {members.map((member) => {
        const isSelf = member.user_id === currentUserId;
        const isMemberOwner = member.role === 'owner';

        return (
          <li
            key={member.user_id}
            className="flex items-center justify-between py-3 px-2"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {member.username}
              </span>
              {isMemberOwner ? (
                <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                  <Crown className="w-3 h-3" aria-hidden="true" />
                  Owner
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                  <User className="w-3 h-3" aria-hidden="true" />
                  Member
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Owner can remove non-owner members */}
              {isOwner && !isMemberOwner && (
                <button
                  onClick={() => onRemoveMember(member.user_id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                  aria-label={`Remove ${member.username}`}
                >
                  <UserMinus className="w-3 h-3" aria-hidden="true" />
                  Remove
                </button>
              )}

              {/* Non-owner members see Leave button on their own row */}
              {!isOwner && isSelf && (
                <button
                  onClick={onLeaveTeam}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors"
                  aria-label="Leave Team"
                >
                  <LogOut className="w-3 h-3" aria-hidden="true" />
                  Leave Team
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default TeamMemberList;
